import { format, formatDistanceToNow, parseISO } from 'date-fns'

const rupees = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
})

export const money = (value) => rupees.format(Number(value ?? 0))

/** Quantities are decimals from the API; drop trailing zeros. */
export const qty = (value) => {
  const n = Number(value ?? 0)
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(3)))
}

/**
 * A timestamp from the API, as an instant in time.
 *
 * The server and database run on UTC, and the API sends timestamps with no
 * zone on them ('2026-09-30T06:35:00'). parseISO would read that as local
 * time, so it is marked UTC here and format() shows it in the browser's own
 * timezone. A plain date ('2026-09-30') has no time to shift and is left as
 * the calendar day it names; a value that already carries a zone is left as
 * it is.
 */
const toInstant = (iso) => {
  const text = String(iso)
  const hasTime = /T\d/.test(text)
  const hasZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(text)
  return parseISO(hasTime && !hasZone ? `${text}Z` : text)
}

export const dateTime = (iso) => (iso ? format(toInstant(iso), 'dd MMM yyyy, HH:mm') : '—')
/** 24 hours after an API timestamp, as an ISO string dateTime() can show. */
export const dayAfter = (iso) => {
  if (!iso) return null
  const text = String(iso)
  const zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(text) ? text : `${text}Z`
  const at = Date.parse(zoned)
  return Number.isNaN(at) ? null : new Date(at + 24 * 60 * 60 * 1000).toISOString()
}

export const dateOnly = (iso) => (iso ? format(toInstant(iso), 'dd MMM yyyy') : '—')
export const relative = (iso) =>
  iso ? formatDistanceToNow(toInstant(iso), { addSuffix: true }) : '—'

export const percent = (value) =>
  value == null ? '—' : `${(Number(value) * 100).toFixed(0)}%`

export const titleCase = (value) =>
  String(value ?? '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

export const ORDER_STATUS_LABELS = {
  new: 'New',
  pending: 'Pending',
  confirmed: 'Confirmed',
  packed: 'Packed',
  partially_delivered: 'Partially Delivered',
  dispatched: 'Dispatched',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

export const DELIVERY_STATUS_LABELS = {
  pending: 'Pending',
  scheduled: 'Scheduled',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  failed: 'Failed',
  returned: 'Returned',
}

export const SOURCE_LABELS = {
  whatsapp_text: 'Text',
  whatsapp_audio: 'Voice note',
  whatsapp_image: 'Photo',
  manual: 'Manual',
}

export const SOURCE_ICONS = {
  whatsapp_text: '💬',
  whatsapp_audio: '🎤',
  whatsapp_image: '📷',
  manual: '⌨️',
}

/** Next statuses an operator may move an order to — mirrors the backend. */
export const NEXT_STATUSES = {
  new: ['pending', 'confirmed', 'cancelled'],
  pending: ['confirmed', 'cancelled'],
  confirmed: ['packed', 'cancelled'],
  packed: ['dispatched', 'cancelled'],
  // Dispatched here sends everything still to go, as the last shipment.
  partially_delivered: ['dispatched', 'cancelled'],
  dispatched: ['delivered', 'cancelled'],
  delivered: [],
  cancelled: [],
}
